import {
  individualAssuranceComplete,
  individualAssuranceIdProofingStatusGet,
} from '@api/selfServiceServices';
import { type IdProofingSessionStartResponse, IdProofingStatus } from '@api/types';
import ContinueButton from '@components/continueButton/ContinueButton';
import { ErrorCode } from '@errorHandler/constants';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { Form } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssuranceContext } from '../EpcsIndividualFlow';

/** How often the proofing session is re-checked while the clinician works in the vendor window. */
const ID_PROOFING_POLL_INTERVAL_MS = 2_000;
/** How long to keep re-checking before giving up on the proofing session. */
const ID_PROOFING_POLL_TIMEOUT_MS = 5 * 60 * 1_000;

const TRANSLATION = 'self-service.epcs-setup.individual.identity';

interface ProofingPollProps {
  session: IdProofingSessionStartResponse;
  ctx: AssuranceContext;
  cancelFlow: () => void;
  completeFlow: () => void;
}

/**
 * Identity proofing happens in a window this app does not control, so its outcome can only be
 * observed by polling — the one case where an effect is the right tool.
 */
const ProofingPoll = ({ session, ctx, cancelFlow, completeFlow }: ProofingPollProps) => {
  const { emitError } = useErrorContext();

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + ID_PROOFING_POLL_TIMEOUT_MS;

    const check = async () => {
      try {
        const { idProofingStatusType } = await individualAssuranceIdProofingStatusGet(
          { assuranceId: ctx.assuranceId, idProofingSessionToken: session.idProofingSessionToken },
          ctx.workflowId,
          ctx.tenantId,
        );
        if (cancelled) {
          return;
        }
        if (idProofingStatusType === IdProofingStatus.SUCCEEDED) {
          await individualAssuranceComplete(
            { assuranceId: ctx.assuranceId },
            ctx.workflowId,
            ctx.tenantId,
          );
          if (!cancelled) {
            completeFlow();
          }
          return;
        }
        if (idProofingStatusType === IdProofingStatus.FAILED) {
          emitError(new AppError({ code: ErrorCode.ID_PROOFING_FAILED }));
          cancelFlow();
          return;
        }
        if (idProofingStatusType === IdProofingStatus.EXPIRED) {
          emitError(new AppError({ code: ErrorCode.ID_PROOFING_TIMEOUT }));
          cancelFlow();
          return;
        }
        if (Date.now() >= deadline) {
          emitError(new AppError({ code: ErrorCode.ID_PROOFING_TIMEOUT }));
          cancelFlow();
          return;
        }
        timer = setTimeout(() => void check(), ID_PROOFING_POLL_INTERVAL_MS);
      } catch (error) {
        if (cancelled) {
          return;
        }
        emitError(new AppError(error as ApiError));
        cancelFlow();
      }
    };

    void check();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ctx, session.idProofingSessionToken, emitError, cancelFlow, completeFlow]);

  return null;
};

interface EpcsIndividualVerifyIdentityProps {
  session?: IdProofingSessionStartResponse;
  ctx: AssuranceContext;
  /** True while the proofing session is being created, so Continue cannot open two of them. */
  busy: boolean;
  onStart: () => void;
  cancelFlow: () => void;
  completeFlow: () => void;
}

const EpcsIndividualVerifyIdentity = ({
  session,
  ctx,
  busy,
  onStart,
  cancelFlow,
  completeFlow,
}: EpcsIndividualVerifyIdentityProps) => {
  const { t } = useTranslation();

  return (
    <Form className="continue-layout" onFinish={onStart}>
      <div>
        <h2 className="title">{t(`${TRANSLATION}.title`)}</h2>
        <p>{t(`${TRANSLATION}.instructions`)}</p>
        <p>{t(`${TRANSLATION}.warning`)}</p>
      </div>
      {session && (
        <ProofingPoll
          session={session}
          ctx={ctx}
          cancelFlow={cancelFlow}
          completeFlow={completeFlow}
        />
      )}

      <div>
        <ContinueButton
          htmlType="submit"
          loading={busy || !!session}
          secondaryButton={{ label: t('common.do-later'), onClick: cancelFlow }}
        >
          {t('common.continue-button')}
        </ContinueButton>
      </div>
    </Form>
  );
};

export default EpcsIndividualVerifyIdentity;
